import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, FormText, Row } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, isNumber, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getPatientConditions } from 'app/entities/patient-condition/patient-condition.reducer';
import { getEntities as getLabPrescriptions } from 'app/entities/lab-prescription/lab-prescription.reducer';
import { getEntities as getVitals } from 'app/entities/vitals/vitals.reducer';
import { getEntities as getDoctors } from 'app/entities/doctor/doctor.reducer';
import { getEntities as getAppointments } from 'app/entities/appointments/appointments.reducer';
import { getEntities as getAdmissions } from 'app/entities/admission/admission.reducer';
import { ClinicalStatus } from 'app/shared/model/enumerations/clinical-status.model';
import { VerificationStatus } from 'app/shared/model/enumerations/verification-status.model';
import { ConditionCategory } from 'app/shared/model/enumerations/condition-category.model';
import { ConditionSeverity } from 'app/shared/model/enumerations/condition-severity.model';
import { createEntity, getEntity, reset, updateEntity } from './patient-condition-check-up.reducer';

export const PatientConditionCheckUpUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const patientConditions = useAppSelector(state => state.patientCondition.entities);
  const labPrescriptions = useAppSelector(state => state.labPrescription.entities);
  const vitals = useAppSelector(state => state.vitals.entities);
  const doctors = useAppSelector(state => state.doctor.entities);
  const appointments = useAppSelector(state => state.appointments.entities);
  const admissions = useAppSelector(state => state.admission.entities);
  const patientConditionCheckUpEntity = useAppSelector(state => state.patientConditionCheckUp.entity);
  const loading = useAppSelector(state => state.patientConditionCheckUp.loading);
  const updating = useAppSelector(state => state.patientConditionCheckUp.updating);
  const updateSuccess = useAppSelector(state => state.patientConditionCheckUp.updateSuccess);
  const clinicalStatusValues = Object.keys(ClinicalStatus);
  const verificationStatusValues = Object.keys(VerificationStatus);
  const conditionCategoryValues = Object.keys(ConditionCategory);
  const conditionSeverityValues = Object.keys(ConditionSeverity);

  const handleClose = () => {
    navigate(`/patient-condition-check-up${location.search}`);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getPatientConditions({}));
    dispatch(getLabPrescriptions({}));
    dispatch(getVitals({}));
    dispatch(getDoctors({}));
    dispatch(getAppointments({}));
    dispatch(getAdmissions({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    if (values.microSec !== undefined && typeof values.microSec !== 'number') {
      values.microSec = Number(values.microSec);
    }
    if (values.lastupdated !== undefined && typeof values.lastupdated !== 'number') {
      values.lastupdated = Number(values.lastupdated);
    }
    if (values.timestampentry !== undefined && typeof values.timestampentry !== 'number') {
      values.timestampentry = Number(values.timestampentry);
    }
    if (values.cursor !== undefined && typeof values.cursor !== 'number') {
      values.cursor = Number(values.cursor);
    }

    const entity = {
      ...patientConditionCheckUpEntity,
      ...values,
      patientCondition: patientConditions.find(it => it.id.toString() === values.patientCondition?.toString()),
      diagnosisReport: labPrescriptions.find(it => it.id.toString() === values.diagnosisReport?.toString()),
      vitals: vitals.find(it => it.id.toString() === values.vitals?.toString()),
      reportedBy: doctors.find(it => it.id.toString() === values.reportedBy?.toString()),
      encounter: appointments.find(it => it.id.toString() === values.encounter?.toString()),
      ipEncounter: admissions.find(it => it.id.toString() === values.ipEncounter?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {}
      : {
          clinicalStatus: 'ACTIVE',
          verificationStatus: 'UNCONFIRMED',
          conditionCategory: 'PROBLEM_LIST_ITEM',
          conditionSeverity: 'SCT_24484000',
          ...patientConditionCheckUpEntity,
          patientCondition: patientConditionCheckUpEntity?.patientCondition?.id,
          diagnosisReport: patientConditionCheckUpEntity?.diagnosisReport?.id,
          vitals: patientConditionCheckUpEntity?.vitals?.id,
          reportedBy: patientConditionCheckUpEntity?.reportedBy?.id,
          encounter: patientConditionCheckUpEntity?.encounter?.id,
          ipEncounter: patientConditionCheckUpEntity?.ipEncounter?.id,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="tinyMedicRestApp.patientConditionCheckUp.home.createOrEditLabel" data-cy="PatientConditionCheckUpCreateUpdateHeading">
            <Translate contentKey="tinyMedicRestApp.patientConditionCheckUp.home.createOrEditLabel">
              Create or edit a PatientConditionCheckUp
            </Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="patient-condition-check-up-id"
                  label={translate('tinyMedicRestApp.patientConditionCheckUp.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('tinyMedicRestApp.patientConditionCheckUp.active')}
                id="patient-condition-check-up-active"
                name="active"
                data-cy="active"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.patientConditionCheckUp.microSec')}
                id="patient-condition-check-up-microSec"
                name="microSec"
                data-cy="microSec"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.patientConditionCheckUp.lastupdated')}
                id="patient-condition-check-up-lastupdated"
                name="lastupdated"
                data-cy="lastupdated"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.patientConditionCheckUp.timestampentry')}
                id="patient-condition-check-up-timestampentry"
                name="timestampentry"
                data-cy="timestampentry"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.patientConditionCheckUp.cursor')}
                id="patient-condition-check-up-cursor"
                name="cursor"
                data-cy="cursor"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.patientConditionCheckUp.stageType')}
                id="patient-condition-check-up-stageType"
                name="stageType"
                data-cy="stageType"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  maxLength: { value: 100, message: translate('entity.validation.maxlength', { max: 100 }) },
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.patientConditionCheckUp.observationSummary')}
                id="patient-condition-check-up-observationSummary"
                name="observationSummary"
                data-cy="observationSummary"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  maxLength: { value: 500, message: translate('entity.validation.maxlength', { max: 500 }) },
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.patientConditionCheckUp.clinicalStatus')}
                id="patient-condition-check-up-clinicalStatus"
                name="clinicalStatus"
                data-cy="clinicalStatus"
                type="select"
              >
                {clinicalStatusValues.map(clinicalStatus => (
                  <option value={clinicalStatus} key={clinicalStatus}>
                    {translate(`tinyMedicRestApp.ClinicalStatus.${clinicalStatus}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('tinyMedicRestApp.patientConditionCheckUp.verificationStatus')}
                id="patient-condition-check-up-verificationStatus"
                name="verificationStatus"
                data-cy="verificationStatus"
                type="select"
              >
                {verificationStatusValues.map(verificationStatus => (
                  <option value={verificationStatus} key={verificationStatus}>
                    {translate(`tinyMedicRestApp.VerificationStatus.${verificationStatus}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('tinyMedicRestApp.patientConditionCheckUp.conditionCategory')}
                id="patient-condition-check-up-conditionCategory"
                name="conditionCategory"
                data-cy="conditionCategory"
                type="select"
              >
                {conditionCategoryValues.map(conditionCategory => (
                  <option value={conditionCategory} key={conditionCategory}>
                    {translate(`tinyMedicRestApp.ConditionCategory.${conditionCategory}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('tinyMedicRestApp.patientConditionCheckUp.conditionSeverity')}
                id="patient-condition-check-up-conditionSeverity"
                name="conditionSeverity"
                data-cy="conditionSeverity"
                type="select"
              >
                {conditionSeverityValues.map(conditionSeverity => (
                  <option value={conditionSeverity} key={conditionSeverity}>
                    {translate(`tinyMedicRestApp.ConditionSeverity.${conditionSeverity}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                id="patient-condition-check-up-patientCondition"
                name="patientCondition"
                data-cy="patientCondition"
                label={translate('tinyMedicRestApp.patientConditionCheckUp.patientCondition')}
                type="select"
                required
              >
                <option value="" key="0" />
                {patientConditions
                  ? patientConditions.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <FormText>
                <Translate contentKey="entity.validation.required">This field is required.</Translate>
              </FormText>
              <ValidatedField
                id="patient-condition-check-up-diagnosisReport"
                name="diagnosisReport"
                data-cy="diagnosisReport"
                label={translate('tinyMedicRestApp.patientConditionCheckUp.diagnosisReport')}
                type="select"
                required
              >
                <option value="" key="0" />
                {labPrescriptions
                  ? labPrescriptions.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <FormText>
                <Translate contentKey="entity.validation.required">This field is required.</Translate>
              </FormText>
              <ValidatedField
                id="patient-condition-check-up-vitals"
                name="vitals"
                data-cy="vitals"
                label={translate('tinyMedicRestApp.patientConditionCheckUp.vitals')}
                type="select"
              >
                <option value="" key="0" />
                {vitals
                  ? vitals.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="patient-condition-check-up-reportedBy"
                name="reportedBy"
                data-cy="reportedBy"
                label={translate('tinyMedicRestApp.patientConditionCheckUp.reportedBy')}
                type="select"
              >
                <option value="" key="0" />
                {doctors
                  ? doctors.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="patient-condition-check-up-encounter"
                name="encounter"
                data-cy="encounter"
                label={translate('tinyMedicRestApp.patientConditionCheckUp.encounter')}
                type="select"
              >
                <option value="" key="0" />
                {appointments
                  ? appointments.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="patient-condition-check-up-ipEncounter"
                name="ipEncounter"
                data-cy="ipEncounter"
                label={translate('tinyMedicRestApp.patientConditionCheckUp.ipEncounter')}
                type="select"
              >
                <option value="" key="0" />
                {admissions
                  ? admissions.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/patient-condition-check-up" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button color="primary" id="save-entity" data-cy="entityCreateSaveButton" type="submit" disabled={updating}>
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default PatientConditionCheckUpUpdate;
